import { Content } from '@wajeez-web/core/api-base';
import {
  FieldValues,
  UseControllerProps,
  UseFormSetError,
} from 'react-hook-form';

import { FormFieldV2 } from './Types';
import {
  FormTextInputV2,
  FormTextAreaV2,
  FormTinyMCEInputV2,
  FormDatePickerV2Input,
  FormSwitchInputV2,
  FormNumberInputV2,
  FormAutocompleteV2,
  FormAutocompleteGroupedV2,
  FormUploadImageInputV2,
} from './InputsV2';
import FormUploadInput from './Inputs/FormUploadInput';
import FormCallBackSelectInput from './Inputs/FormCallBackSelectInput';
import FormFileDragAndDropInput from './Inputs/FormFileDragAndDropInput';
import FormColorPickerInput from './Inputs/FormColorPickerInput';
import FormTinyMCEInput from './Inputs/FormTinyMCEInput';

export interface WajeezFormInputV2Props<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldMeta: FormFieldV2;
  // onChange?: (...values: (string | null | boolean | number)[]) => void;
  isReadOnly?: boolean;
  setError?: UseFormSetError<TFormFields>;
  validationErrorMessages?: Partial<{
    required: string;
    pattern: string;
    minLength: string;
    maxLength: string;
  }>;
  id?: string;
}

const WajeezFormInputV2 = <TFormFields extends FieldValues>({
  fieldMeta,
  isReadOnly,
  // onChange,
  ...props
}: WajeezFormInputV2Props<TFormFields>) => {
  const { fieldType, fieldOptions } = fieldMeta;

  switch (fieldType) {
    case Content.FieldType.MappedField:
      return (
        <FormAutocompleteGroupedV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.PredefinedComponent:
      return (
        <FormAutocompleteV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.RequiredComponent:
      return (
        <FormAutocompleteV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.UploadFile:
      return (
        <FormUploadInput
          disabled={isReadOnly}
          fieldType={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.CallbackSelect:
      return (
        <FormCallBackSelectInput
          disabled={isReadOnly}
          fieldType={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.TineMCE:
      return (
        <FormTinyMCEInput
          disabled={isReadOnly}
          fieldType={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.UploadImage:
      return (
        <FormUploadImageInputV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.Switch:
      return (
        <FormSwitchInputV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.Number:
      return (
        <FormNumberInputV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.Select:
      return (
        <FormAutocompleteV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.DragFile:
      return (
        <FormFileDragAndDropInput
          disabled={isReadOnly}
          fieldType={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.Date:
      return (
        <FormDatePickerV2Input
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          showTime={false}
          {...props}
        />
      );
    case Content.FieldType.DateTime:
      return (
        <FormDatePickerV2Input
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          showTime={true}
          {...props}
        />
      );
    case Content.FieldType.AreaText:
      return (
        <FormTextAreaV2
          disabled={isReadOnly}
          fieldMeta={fieldMeta}
          {...props}
        />
      );
    case Content.FieldType.Text:
      return (
        <FormTextInputV2
          readOnly={isReadOnly}
          fieldMeta={fieldMeta}
          // onChange={onChange}
          {...props}
        />
      );
    case Content.FieldType.ColorPicker:
      return (
        <FormColorPickerInput
          disabled={isReadOnly}
          fieldType={fieldMeta}
          colors={fieldOptions?.colors}
          pickerType={fieldOptions?.pickerType}
          {...props}
        />
      );

    default:
      return null;
  }
};

export default WajeezFormInputV2;
